/**
 * DataChannel — BroadcastChannel methods for Web Components.
 *
 * The canonical consumer pattern for Issue #94's pure-broadcast
 * architecture. Components import the methods they need and attach them
 * as class fields rather than `extends DataChannelMixin(HTMLElement)`.
 * Function references hoist fine across the bundle; `extends MixinFn(...)`
 * doesn't.
 *
 * Listener envelope assumed (Phase 2 — bare payload):
 *   { type: '<event>', data: <bare payload> }
 *
 * Routing happens on `event.data.type`. Handlers receive `event.data.data`
 * — the bare entity for create/update, or `{ id }` for deletes.
 *
 * Usage:
 *
 *   import { setupChannel, destroyChannel, lubooRequest } from '../mixins/data-channel.mixin.js';
 *
 *   class MyFeature extends HTMLElement {
 *     setupChannel = setupChannel;       // attaches the method
 *     destroyChannel = destroyChannel;
 *
 *     _messageMap = {
 *       booking_created: this._onBookingCreated,
 *     };
 *
 *     connectedCallback() {
 *       this.setupChannel(`luboo:property:${this.propertyId}`);
 *     }
 *
 *     disconnectedCallback() {
 *       this.destroyChannel();
 *     }
 *
 *     _onBookingCreated(booking) {
 *       // booking is the bare entity dict
 *     }
 *   }
 */

/**
 * UUID v4. crypto.randomUUID() is HTTPS/localhost only; HTTP Tailscale
 * (phone) throws and calendar/map never load.
 */
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Subscribe to a BroadcastChannel; route incoming messages via _messageMap. */
function setupChannel(channelName) {
  if (this._channel) {
    console.warn(`[${this.constructor.name}] Channel already set up; closing previous: ${this._channelName}`);
    this.destroyChannel();
  }
  this._channelName = channelName;
  this._channel = new BroadcastChannel(channelName);
  this._channel.onmessage = (event) => _routeMessage.call(this, event);
}

/** Close the subscribed channel and clear state. */
function destroyChannel() {
  if (this._channel) {
    this._channel.close();
    this._channel = null;
    this._channelName = null;
  }
}

/** Internal: route an incoming message to the matching _messageMap handler. */
function _routeMessage(event) {
  const eventName = event.data?.type;
  const handler = this._messageMap?.[eventName];
  if (handler) {
    handler.call(this, event.data?.data);
  }
}

/**
 * Standalone request-with-response helper for any caller.
 *
 * Mirrors LubooData.sendRequest: posts on the named channel with a private
 * response channel, returns a promise that resolves with the data layer's
 * response (or rejects on timeout / handler error).
 *
 * @param {string} channelName - e.g. 'luboo:api', `luboo:property:${id}`
 * @param {string} type - request type (e.g. 'upload_avatar_request')
 * @param {object} [data] - request payload
 * @param {{timeout?: number, coalesce?: boolean, group?: string}} [options]
 * @returns {Promise<*>} resolves with response data, rejects on error/timeout
 */
async function lubooRequest(channelName, type, data = {}, options = {}) {
  const { coalesce = false, timeout = 5000, group = null } = options;

  // Fail loud on the two most common caller-side bugs that would otherwise
  // 15s-time-out into the developer's lap as "Request timeout: <type>":
  //   1. Channel name contains the literal "undefined"/"null" — caller passed
  //      a null id (e.g. `luboo:account:${currentAccount}` while currentAccount
  //      hadn't loaded). This was today's accounts-init regression.
  //   2. Data layer has no listener bound to the channel — caller mistyped
  //      a propertyId, or asked for a channel that was never set up.
  // Both used to silently swallow the post and time out 15s later.
  if (/:(undefined|null)\b/.test(channelName)) {
    throw new Error(
      `lubooRequest: refusing to post to malformed channel "${channelName}" `
        + `(type=${type}${group ? ` group=${group}` : ''}). Caller passed null/undefined in the channel name.`,
    );
  }
  const dataLayer = (typeof window !== 'undefined') ? window.luboo?.data : null;
  if (dataLayer?.hasChannel && !dataLayer.hasChannel(channelName)) {
    throw new Error(
      `lubooRequest: no data-layer listener on "${channelName}" `
        + `(type=${type}${group ? ` group=${group}` : ''}). Channel was never set up; ensure the right shell init ran first.`,
    );
  }

  const responseChannel = `luboo:response:${generateUUID()}`;
  const resChannel = new BroadcastChannel(responseChannel);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      resChannel.close();
      reject(new Error(`Request timeout: ${type}`));
    }, timeout);

    resChannel.onmessage = (event) => {
      clearTimeout(timer);
      resChannel.close();
      if (event.data.success) {
        resolve(event.data.data);
      } else {
        const err = new Error(event.data.error);
        if (event.data.status !== undefined) err.status = event.data.status;
        if (event.data.detail !== undefined) err.detail = event.data.detail;
        reject(err);
      }
    };

    const channel = new BroadcastChannel(channelName);
    channel.postMessage({ type, group, data, coalesce, responseChannel });
    channel.close();
  });
}

export { setupChannel, destroyChannel, _routeMessage, lubooRequest };
